import React, { useState, useEffect } from 'react';
import { Camera, Heart, X, Download, Share2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { getToken } from '../../utils/tokenStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhotoMode = ({ canvasRef, isOpen, onClose, user, isGuest }) => {
  const [photos, setPhotos] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [caption, setCaption] = useState('');
  const [showGallery, setShowGallery] = useState(false);
  const [flashEffect, setFlashEffect] = useState(false);

  useEffect(() => {
    if (isOpen) fetchPhotos();
  }, [isOpen]);

  const fetchPhotos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/nightclub/photos`);
      if (res.ok) setPhotos(await res.json());
    } catch (e) { /* silent */ }
  };

  const capturePhoto = async () => {
    if (!canvasRef?.current || isGuest) {
      if (isGuest) toast.error('Sign in to take photos');
      return;
    }

    setCapturing(true);
    setFlashEffect(true);
    setTimeout(() => setFlashEffect(false), 300);

    try {
      const imageData = canvasRef.current.toDataURL('image/jpeg', 0.7);
      const token = getToken();
      const res = await fetch(`${API_URL}/api/nightclub/photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ image_data: imageData, caption })
      });
      
      if (res.ok) {
        toast.success('Photo captured!');
        setCaption('');
        fetchPhotos();
      }
    } catch (e) {
      toast.error('Failed to capture photo');
    } finally {
      setCapturing(false);
    }
  };

  const handleLike = async (photoId) => {
    if (isGuest) { toast.error('Sign in to like photos'); return; }
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/nightclub/photo/${photoId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchPhotos();
    } catch (e) { /* silent */ }
  };

  const downloadPhoto = (imageData) => {
    const link = document.createElement('a');
    link.download = `razient-nightclub-${Date.now()}.jpg`;
    link.href = imageData;
    link.click();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Flash effect */}
      {flashEffect && (
        <div className="fixed inset-0 z-[100] bg-white pointer-events-none animate-pulse" style={{ animationDuration: '0.3s' }} />
      )}
      
      <div className="absolute top-16 right-4 z-40 w-80" data-testid="photo-mode-panel">
        <div className="glass-heavy rounded-xl border border-white/10 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Camera className="w-4 h-4 text-violet-400" />
              <span className="font-semibold text-sm">Photo Mode</span>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setShowGallery(!showGallery)} 
                className={`text-xs h-7 ${showGallery ? 'bg-violet-600' : ''}`}>
                Gallery ({photos.length})
              </Button>
              <button onClick={onClose} className="text-white/50 hover:text-white p-1">
                <X size={16} />
              </button>
            </div>
          </div>

          {!showGallery ? (
            /* Capture Mode */
            <div className="p-3 space-y-3">
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption..."
                className="bg-white/5 border-white/10 text-sm h-8"
                maxLength={100}
                data-testid="photo-caption-input"
              />
              <Button
                onClick={capturePhoto}
                disabled={capturing || isGuest}
                className="w-full h-10 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 font-bold"
                data-testid="capture-photo-btn"
              >
                {capturing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
                {capturing ? 'Capturing...' : 'Take Photo'}
              </Button>
              {isGuest && <p className="text-xs text-white/40 text-center">Sign in to use Photo Mode</p>}
            </div>
          ) : (
            /* Gallery */
            <div className="max-h-80 overflow-y-auto p-2 space-y-2">
              {photos.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-4">No photos yet. Be the first!</p>
              ) : photos.map((photo) => (
                <div key={photo.photo_id} className="rounded-lg overflow-hidden border border-white/5 bg-white/5">
                  <img src={photo.image_data} alt="Nightclub" className="w-full aspect-video object-cover" />
                  <div className="p-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/60">{photo.user_name}</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleLike(photo.photo_id)} 
                          className={`flex items-center gap-1 text-xs ${photo.liked_by?.includes(user?.user_id) ? 'text-red-400' : 'text-white/40 hover:text-red-400'}`}>
                          <Heart size={12} className={photo.liked_by?.includes(user?.user_id) ? 'fill-current' : ''} />
                          {photo.likes || 0}
                        </button>
                        <button onClick={() => downloadPhoto(photo.image_data)} className="text-white/40 hover:text-white">
                          <Download size={12} />
                        </button>
                      </div>
                    </div>
                    {photo.caption && <p className="text-xs text-white/50 mt-1">{photo.caption}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PhotoMode;
